import { Schema, type NodeSpec, type MarkSpec } from 'prosemirror-model';
import { PageType } from '../types.js';

/**
 * The single canonical ProseMirror schema (architecture §3, IMPL-01).
 *
 * One schema serves both presets. It is intentionally CLOSED and explicit — an open
 * schema makes the markdown contract impossible to guarantee (IMPL-01 Notes). Rich
 * nodes (callout, table, image, imageGrid, mermaid, math) are declared here from the
 * start so the schema never changes shape between phases; their *behavior* and
 * serializers are filled in by later specs, but they live in the schema now.
 *
 * Document-level attributes (pageType, icon, banner, tags) ride on the doc node so
 * tags and chrome never clutter the body.
 */

/** Read the shared block-meta attrs (id + planning dates) off a parsed <p>/<h*> element. */
function readBlockMeta(el: HTMLElement): { id: string | null; due: string | null; scheduled: string | null } {
  return {
    id: el.getAttribute('data-id') || null,
    due: el.getAttribute('data-due') || null,
    scheduled: el.getAttribute('data-scheduled') || null,
  };
}

/** Serialize the block-meta attrs to data-* attributes, omitting any that are unset. */
function blockMetaDOM(attrs: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  if (attrs.id) out['data-id'] = attrs.id as string;
  if (attrs.due) out['data-due'] = attrs.due as string;
  if (attrs.scheduled) out['data-scheduled'] = attrs.scheduled as string;
  return out;
}

const nodes: Record<string, NodeSpec> = {
  doc: {
    content: 'block+',
    attrs: {
      pageType: { default: PageType.Document },
      icon: { default: null },
      banner: { default: null },
      bannerAlign: { default: null },
      bannerAlignX: { default: null },
      bannerZoom: { default: null },
      bannerHeight: { default: null },
      tags: { default: [] as string[] },
      aliases: { default: [] as string[] },
    },
  },

  // `id` is the stable block identity (IMPL-07, Logseq block refs/embeds). Default null so existing
  // content stays valid (a required id would make every old block "invalid" and risk y-prosemirror's
  // in-place repair). Assigned lazily client-side by the block-id plugin; round-trips to markdown as
  // a trailing `<!-- id: … -->` marker. Carried as data-id through the generic schema→TipTap adapter.
  paragraph: {
    group: 'block',
    content: 'inline*',
    attrs: { id: { default: null }, due: { default: null }, scheduled: { default: null } },
    parseDOM: [{ tag: 'p', getAttrs: (el) => readBlockMeta(el as HTMLElement) }],
    toDOM: (node) => ['p', blockMetaDOM(node.attrs), 0],
  },

  // `due`/`scheduled` are optional ISO (YYYY-MM-DD) planning dates on a block — the native replacement
  // for Logseq's DEADLINE:/SCHEDULED: lines (IMPL: block dates). They ride the same `<!-- … -->` trailer
  // as `id` through markdown (see serialize/parse), render as pills in the editor, and default null so
  // existing content stays valid.
  heading: {
    group: 'block',
    content: 'inline*',
    attrs: { level: { default: 1 }, id: { default: null }, due: { default: null }, scheduled: { default: null } },
    defining: true,
    parseDOM: [1, 2, 3, 4, 5, 6].map((level) => ({
      tag: `h${level}`,
      getAttrs: (el) => ({ level, ...readBlockMeta(el as HTMLElement) }),
    })),
    toDOM: (node) => [`h${node.attrs.level}`, blockMetaDOM(node.attrs), 0],
  },

  blockquote: {
    group: 'block',
    content: 'block+',
    defining: true,
    parseDOM: [{ tag: 'blockquote' }],
    toDOM: () => ['blockquote', 0],
  },

  // Callout: rich block, lossy round-trip (serialized to a blockquote fallback + flagged).
  callout: {
    group: 'block',
    content: 'block+',
    defining: true,
    attrs: { kind: { default: 'note' } },
    parseDOM: [{ tag: 'div[data-callout]', getAttrs: (el) => ({ kind: (el as HTMLElement).getAttribute('data-callout') || 'note' }) }],
    toDOM: (node) => ['div', { 'data-callout': node.attrs.kind, class: `callout callout-${node.attrs.kind}` }, 0],
  },

  // Collapsible disclosure (GRAM-05, D14 "Show original"). A distinct node rather than a callout
  // variant: `summary`/`collapsed` are structural state that round-trips via the `:::details`
  // directive, while callout's `kind` is purely presentational (decision recorded per spec R4).
  disclosure: {
    group: 'block',
    content: 'block+',
    defining: true,
    attrs: { summary: { default: 'Details' }, collapsed: { default: true } },
    parseDOM: [
      {
        tag: 'div[data-disclosure]',
        getAttrs: (el) => ({
          summary: (el as HTMLElement).getAttribute('data-summary') || 'Details',
          collapsed: (el as HTMLElement).getAttribute('data-collapsed') !== 'false',
        }),
      },
    ],
    toDOM: (node) => [
      'div',
      {
        'data-disclosure': '',
        'data-summary': node.attrs.summary as string,
        'data-collapsed': String(node.attrs.collapsed),
        class: 'disclosure',
      },
      0,
    ],
  },

  codeBlock: {
    group: 'block',
    content: 'text*',
    marks: '',
    code: true,
    defining: true,
    attrs: { language: { default: null } },
    parseDOM: [{ tag: 'pre', preserveWhitespace: 'full' }],
    toDOM: (node) => ['pre', { 'data-language': node.attrs.language }, ['code', 0]],
  },

  // Mermaid diagram: round-trips cleanly to a ```mermaid fence (IMPL-16).
  mermaid: {
    group: 'block',
    content: 'text*',
    marks: '',
    code: true,
    defining: true,
    parseDOM: [{ tag: 'div[data-mermaid]', preserveWhitespace: 'full' }],
    toDOM: () => ['div', { 'data-mermaid': '' }, 0],
  },

  // Block math: round-trips to $$ ... $$ (IMPL-16).
  mathBlock: {
    group: 'block',
    content: 'text*',
    marks: '',
    code: true,
    defining: true,
    parseDOM: [{ tag: 'div[data-math-block]', preserveWhitespace: 'full' }],
    toDOM: () => ['div', { 'data-math-block': '' }, 0],
  },

  bulletList: {
    group: 'block',
    content: 'listItem+',
    parseDOM: [{ tag: 'ul' }],
    toDOM: () => ['ul', 0],
  },

  orderedList: {
    group: 'block',
    content: 'listItem+',
    attrs: { order: { default: 1 } },
    parseDOM: [{ tag: 'ol', getAttrs: (el) => ({ order: +((el as HTMLElement).getAttribute('start') || 1) }) }],
    toDOM: (node) => (node.attrs.order === 1 ? ['ol', 0] : ['ol', { start: node.attrs.order }, 0]),
  },

  // listItem carries `collapsed` for journal collapse (IMPL-06). Content is `block+` (Logseq-style:
  // any block can be a bullet's own line — a heading/quote/code, not just a paragraph), matching the
  // editor schema. If this were stricter than the editor, heading-first bullets pasted into the
  // editor would be valid there but stripped when read back through the core schema (markdown mirror,
  // ingest), silently dropping content.
  // `marker` picks the bullet glyph: the default 'bullet' dot, or 'number' for a per-item ordered
  // marker (1./2./3.) rendered via CSS counters (see styles.css). It is a VIEW-ONLY affordance and is
  // deliberately NOT serialized to ordered markdown — a numbered outline bullet still round-trips as a
  // `-` list item (the schema stays a single bulletList outliner). Default 'bullet' keeps every
  // existing document valid, same reasoning as the `id` default-null above.
  listItem: {
    content: 'block+',
    attrs: { collapsed: { default: false }, marker: { default: 'bullet' } },
    defining: true,
    parseDOM: [
      {
        tag: 'li',
        getAttrs: (el) => ({
          collapsed: (el as HTMLElement).getAttribute('data-collapsed') === 'true',
          marker: (el as HTMLElement).getAttribute('data-marker') === 'number' ? 'number' : 'bullet',
        }),
      },
    ],
    toDOM: (node) => [
      'li',
      {
        ...(node.attrs.collapsed ? { 'data-collapsed': 'true' } : {}),
        ...(node.attrs.marker === 'number' ? { 'data-marker': 'number' } : {}),
      },
      0,
    ],
  },

  horizontalRule: {
    group: 'block',
    parseDOM: [{ tag: 'hr' }],
    toDOM: () => ['hr'],
  },

  // GFM-style table (IMPL-18). Lossy for complex content; flagged.
  table: {
    group: 'block',
    content: 'tableRow+',
    isolating: true,
    parseDOM: [{ tag: 'table' }],
    toDOM: () => ['table', ['tbody', 0]],
  },
  tableRow: {
    content: 'tableCell+',
    parseDOM: [{ tag: 'tr' }],
    toDOM: () => ['tr', 0],
  },
  tableCell: {
    content: 'inline*',
    attrs: { header: { default: false } },
    isolating: true,
    parseDOM: [
      { tag: 'th', attrs: { header: true } },
      { tag: 'td', attrs: { header: false } },
    ],
    toDOM: (node) => [node.attrs.header ? 'th' : 'td', 0],
  },

  // Image: first-class block with resize/crop/caption/link/fit (IMPL-17). Lossy md fallback.
  // crop is a normalized {x,y,width,height} rect in 0..1; width/height are display px;
  // fit is the object-fit used when both dimensions are pinned ('cover' | 'contain').
  // Attribute NAMES are shared with the TipTap editor node so the Yjs fragment round-trips
  // through this schema for the markdown mirror (see core/doc/yjs.ts).
  image: {
    group: 'block',
    draggable: true,
    // A leaf rendered by a node view (like `html`); the editor owns resize/crop/caption UI.
    atom: true,
    attrs: {
      src: { default: '' },
      alt: { default: null },
      title: { default: null },
      width: { default: null },
      height: { default: null },
      crop: { default: null },
      caption: { default: null },
      href: { default: null },
      fit: { default: null },
    },
    parseDOM: [
      {
        // The editor wraps images in <figure data-image>; parse that first so caption,
        // crop, fit, link, and dimensions survive an in-app copy/paste. A bare <img> still
        // parses via the second rule (e.g. HTML pasted from the web).
        tag: 'figure[data-image]',
        getAttrs: (el) => {
          const e = el as HTMLElement;
          const img = e.querySelector('img');
          const link = e.querySelector('a[href]');
          const cropRaw = e.getAttribute('data-crop');
          const num = (v: string | null): number | null => (v != null && v !== '' ? +v : null);
          return {
            src: img?.getAttribute('src') || '',
            alt: img?.getAttribute('alt') || null,
            title: img?.getAttribute('title') || null,
            width: num(e.getAttribute('data-width')),
            height: num(e.getAttribute('data-height')),
            crop: cropRaw ? JSON.parse(cropRaw) : null,
            caption: e.querySelector('figcaption')?.textContent || null,
            href: link?.getAttribute('href') || null,
            fit: e.getAttribute('data-fit') || null,
          };
        },
      },
      {
        tag: 'img[src]',
        getAttrs: (el) => {
          const e = el as HTMLElement;
          return {
            src: e.getAttribute('src') || '',
            alt: e.getAttribute('alt'),
            title: e.getAttribute('title'),
          };
        },
      },
    ],
    toDOM: (node) => {
      const a = node.attrs;
      const fig: Record<string, string> = { 'data-image': '' };
      if (a.width != null) fig['data-width'] = String(a.width);
      if (a.height != null) fig['data-height'] = String(a.height);
      if (a.crop) fig['data-crop'] = JSON.stringify(a.crop);
      if (a.fit) fig['data-fit'] = a.fit as string;
      const img = ['img', { src: a.src, alt: a.alt, title: a.title }];
      const imgOrLink = a.href ? ['a', { href: a.href }, img] : img;
      const children: unknown[] = [imgOrLink];
      if (a.caption) children.push(['figcaption', {}, a.caption]);
      return ['figure', fig, ...children] as never;
    },
  },

  // Multi-column/row image grid — the "gallery" (IMPL-17). Round-trips through markdown via the
  // `:::gallery` directive (GRAM-21); member images keep only src/alt/title in that form, so the
  // image node itself stays lossy-flagged. columns/rows define the CSS grid (rows null =
  // auto-flow); gap is px; fit is the object-fit applied to member images so a gallery reads as
  // an even tile wall.
  imageGrid: {
    group: 'block',
    content: 'image+',
    // Keep selections/deletes from straddling the gallery boundary.
    isolating: true,
    attrs: {
      columns: { default: 2 },
      rows: { default: null },
      gap: { default: 8 },
      fit: { default: 'cover' },
    },
    parseDOM: [
      {
        tag: 'div[data-image-grid]',
        getAttrs: (el) => {
          const e = el as HTMLElement;
          const rows = e.getAttribute('data-rows');
          return {
            columns: +(e.getAttribute('data-columns') || 2),
            rows: rows ? +rows : null,
            gap: +(e.getAttribute('data-gap') || 8),
            fit: e.getAttribute('data-fit') || 'cover',
          };
        },
      },
    ],
    toDOM: (node) => {
      const a = node.attrs;
      const attrs: Record<string, string> = {
        'data-image-grid': '',
        'data-columns': String(a.columns),
        'data-gap': String(a.gap),
        'data-fit': a.fit as string,
      };
      if (a.rows != null) attrs['data-rows'] = String(a.rows);
      return ['div', attrs, 0];
    },
  },

  // Raw HTML block (e.g. pasted `<table>`/`<div>` that has no markdown equivalent). An opaque
  // atom holding the source; the editor renders it via a sanitizing node view, and it round-trips
  // verbatim to markdown. The DOM here only carries the source in an attribute — actual rendering
  // is the UI layer's job (like mermaid/math), so this stays DOM-free.
  html: {
    group: 'block',
    atom: true,
    attrs: { html: { default: '' } },
    parseDOM: [
      {
        tag: 'div[data-raw-html]',
        getAttrs: (el) => ({ html: (el as HTMLElement).getAttribute('data-raw-html') || '' }),
      },
    ],
    toDOM: (node) => ['div', { 'data-raw-html': node.attrs.html, class: 'raw-html' }],
  },

  // Generic file attachment (IMPL-17): PDFs and arbitrary dropped files. `src` carries the
  // stable `lapo://` storage reference (never a signed URL — those are resolved at render time).
  // `kind` is 'pdf' for an inline preview or 'file' for a download chip; images use the image
  // node instead. `width` is the PDF preview's rendered width in px (null ⇒ fit the column); it's
  // ignored for download chips. Atom (no editable content). Lossy md fallback (link), flagged in
  // LOSSY_NODES.
  file: {
    group: 'block',
    atom: true,
    draggable: true,
    attrs: {
      src: { default: '' },
      filename: { default: '' },
      contentType: { default: 'application/octet-stream' },
      size: { default: null },
      kind: { default: 'file' },
      width: { default: null },
    },
    parseDOM: [
      {
        tag: 'a[data-file]',
        getAttrs: (el) => {
          const e = el as HTMLElement;
          const size = e.getAttribute('data-size');
          const width = e.getAttribute('data-width');
          return {
            src: e.getAttribute('data-file') || '',
            filename: e.getAttribute('data-filename') || '',
            contentType: e.getAttribute('data-content-type') || 'application/octet-stream',
            size: size ? Number(size) : null,
            kind: e.getAttribute('data-kind') || 'file',
            width: width ? Number(width) : null,
          };
        },
      },
    ],
    toDOM: (node) => [
      'a',
      {
        'data-file': node.attrs.src,
        'data-filename': node.attrs.filename,
        'data-content-type': node.attrs.contentType,
        'data-size': node.attrs.size == null ? null : String(node.attrs.size),
        'data-kind': node.attrs.kind,
        'data-width': node.attrs.width == null ? null : String(node.attrs.width),
        href: node.attrs.src,
      },
      node.attrs.filename || 'file',
    ],
  },

  text: { group: 'inline' },

  hardBreak: {
    group: 'inline',
    inline: true,
    selectable: false,
    parseDOM: [{ tag: 'br' }],
    toDOM: () => ['br'],
  },

  // Wikilink inline atom (declared IMPL-01; behavior IMPL-07).
  wikilink: {
    group: 'inline',
    inline: true,
    atom: true,
    attrs: { target: { default: '' }, label: { default: null } },
    parseDOM: [
      {
        tag: 'a[data-wikilink]',
        getAttrs: (el) => {
          const e = el as HTMLElement;
          const target = e.getAttribute('data-wikilink') || '';
          const text = e.textContent || '';
          // Only treat the visible text as a label when it differs from the target, so a plain
          // `[[Foo]]` (rendered as "Foo") round-trips back to `[[Foo]]`, not `[[Foo|Foo]]`.
          return { target, label: text && text !== target ? text : null };
        },
      },
    ],
    toDOM: (node) => ['a', { 'data-wikilink': node.attrs.target, class: 'wikilink' }, node.attrs.label || node.attrs.target],
  },

  // Tag inline atom (declared IMPL-01; behavior IMPL-07).
  tag: {
    group: 'inline',
    inline: true,
    atom: true,
    attrs: { name: { default: '' } },
    parseDOM: [
      {
        tag: 'span[data-tag]',
        getAttrs: (el) => ({ name: (el as HTMLElement).getAttribute('data-tag') || '' }),
      },
    ],
    toDOM: (node) => ['span', { 'data-tag': node.attrs.name, class: 'tag' }, `#${node.attrs.name}`],
  },

  // LOOM initiative tag — inline atom inserted by the `+` picker. Attrs carry the stable initiative
  // id (renames are free) plus an optional quarter override and a display name for inert rendering.
  // Serialized as `+[name](loom:ini_x@2026-Q1)`; round-trips and renders as plain text elsewhere.
  // Replaces the old `initiative` node (LOOM D-13); the legacy `+Name` regex parser is deleted so
  // pre-LOOM `+mentions` are inert plain text until re-tagged.
  initiativeTag: {
    group: 'inline',
    inline: true,
    atom: true,
    attrs: { initiativeId: { default: '' }, quarter: { default: null }, name: { default: '' } },
    parseDOM: [
      {
        tag: 'span[data-initiative-tag]',
        getAttrs: (el) => {
          const e = el as HTMLElement;
          return {
            initiativeId: e.getAttribute('data-initiative-tag') || '',
            quarter: e.getAttribute('data-quarter') || null,
            name: e.getAttribute('data-name') || e.textContent?.replace(/^\+/, '') || '',
          };
        },
      },
    ],
    toDOM: (node) => [
      'span',
      {
        'data-initiative-tag': node.attrs.initiativeId,
        'data-quarter': node.attrs.quarter || undefined,
        'data-name': node.attrs.name,
        class: 'initiative-tag',
      },
      `+${node.attrs.name}`,
    ],
  },

  // Block reference inline atom (IMPL-07): `((blockId))` shows another block's text inline. The node
  // view fetches and renders the referenced block; the fallback DOM text keeps it sane without one.
  blockRef: {
    group: 'inline',
    inline: true,
    atom: true,
    attrs: { blockId: { default: '' } },
    parseDOM: [
      {
        tag: 'a[data-block-ref]',
        getAttrs: (el) => ({ blockId: (el as HTMLElement).getAttribute('data-block-ref') || '' }),
      },
    ],
    toDOM: (node) => ['a', { 'data-block-ref': node.attrs.blockId, class: 'block-ref' }, `(( ))`],
  },

  // Embed block atom (IMPL-07): transcludes a page (`{{embed [[Page]]}}`) or a block
  // (`{{embed ((id))}}`). A leaf rendered read-only by a node view (like `html`); the embed stores a
  // pointer, not a copy, so it round-trips to the shortcode without duplicating content.
  embed: {
    group: 'block',
    atom: true,
    attrs: { kind: { default: 'page' }, target: { default: '' } },
    parseDOM: [
      {
        tag: 'div[data-embed]',
        getAttrs: (el) => {
          const e = el as HTMLElement;
          return { kind: e.getAttribute('data-embed-kind') || 'page', target: e.getAttribute('data-embed') || '' };
        },
      },
    ],
    toDOM: (node) => [
      'div',
      { 'data-embed': node.attrs.target, 'data-embed-kind': node.attrs.kind, class: 'embed' },
    ],
  },

  // Bookmark card (link preview): a Notion-style rich link. Block atom rendered by a node view
  // (favicon + title + description + thumbnail). Stores the resolved OG/title metadata so the card
  // survives offline and round-trips to a `:::bookmark` directive with a JSON body. `image` is a
  // stored `lapo://` ref (re-hosted OG image) or an https URL; `favicon` an https URL.
  bookmark: {
    group: 'block',
    atom: true,
    draggable: true,
    attrs: {
      url: { default: '' },
      title: { default: '' },
      description: { default: '' },
      image: { default: '' },
      favicon: { default: '' },
    },
    parseDOM: [
      {
        tag: 'div[data-bookmark]',
        getAttrs: (el) => {
          const e = el as HTMLElement;
          return {
            url: e.getAttribute('data-bookmark') || '',
            title: e.getAttribute('data-title') || '',
            description: e.getAttribute('data-description') || '',
            image: e.getAttribute('data-image') || '',
            favicon: e.getAttribute('data-favicon') || '',
          };
        },
      },
    ],
    toDOM: (node) => [
      'div',
      {
        'data-bookmark': node.attrs.url,
        'data-title': node.attrs.title || null,
        'data-description': node.attrs.description || null,
        'data-image': node.attrs.image || null,
        'data-favicon': node.attrs.favicon || null,
        class: 'bookmark',
      },
    ],
  },

  // Inline math (IMPL-16). Round-trips to $...$.
  mathInline: {
    group: 'inline',
    inline: true,
    atom: true,
    attrs: { latex: { default: '' } },
    parseDOM: [{ tag: 'span[data-math-inline]', getAttrs: (el) => ({ latex: (el as HTMLElement).getAttribute('data-math-inline') || '' }) }],
    toDOM: (node) => ['span', { 'data-math-inline': node.attrs.latex, class: 'math-inline' }, node.attrs.latex],
  },
};

const marks: Record<string, MarkSpec> = {
  strong: {
    parseDOM: [{ tag: 'strong' }, { tag: 'b' }, { style: 'font-weight=bold' }],
    toDOM: () => ['strong', 0],
  },
  em: {
    parseDOM: [{ tag: 'em' }, { tag: 'i' }, { style: 'font-style=italic' }],
    toDOM: () => ['em', 0],
  },
  code: {
    parseDOM: [{ tag: 'code' }],
    toDOM: () => ['code', 0],
  },
  link: {
    attrs: { href: { default: '' }, title: { default: null } },
    inclusive: false,
    parseDOM: [
      {
        tag: 'a[href]:not([data-wikilink])',
        getAttrs: (el) => {
          const e = el as HTMLElement;
          return { href: e.getAttribute('href'), title: e.getAttribute('title') };
        },
      },
    ],
    toDOM: (node) => ['a', { href: node.attrs.href, title: node.attrs.title }, 0],
  },
};

/** The one schema (architecture §3). */
export const lapoSchema = new Schema({ nodes, marks });

/**
 * Node and mark names that do not round-trip cleanly to markdown (architecture §3).
 * imageGrid and disclosure are absent: they round-trip via `:::` directives (GRAM-21).
 */
export const LOSSY_NODES = new Set(['callout', 'image', 'table', 'file']);

export type LapoSchema = typeof lapoSchema;
